import { DocumentDownloadIcon, DownloadIcon } from "assets/icons";
import { onDownloadImage } from "helpers";
import { useBoolean } from "hooks";
import { t } from "i18next";
import toast from "react-hot-toast";
import { Button, PersonSummary, Status, StatusControl } from "ui";

import { useGetDriverContacts, useUpdateDriver } from "../../../drivers/apis";
import { DRIVER_STATUSES_LIST, DriverStatuses } from "../../constants";
import { downloadDriverContacts } from "../../helpers";
import { DriverCertificates, DriverMainInfo } from "./atoms";
import { DriverInfoContentProps } from "./DriverInfoContent.types";

import styles from "./DriverInfoContent.module.scss";

const DriverInfoContent = ({ driver }: DriverInfoContentProps) => {
  const { mutate } = useGetDriverContacts({
    onSuccess(data) {
      downloadDriverContacts(driver, data.contacts, data.createdAt);
    }
  });

  const {
    value: isStatusControlOpen,
    toggle: toggleStatusControl,
    setFalse: closeStatusControl
  } = useBoolean(false);

  const updateDriver = useUpdateDriver({
    onSuccess: () => {
      toast.success(t("Driver status updated successfully"));
      closeStatusControl();
    }
  });

  const statusesList: Status[] = DRIVER_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () =>
      updateDriver.mutate({
        driverId: driver.id,
        isActive: orStatus.value === DriverStatuses.Active
      })
  }));

  const handleDocumentsDownload = async () => {
    await Promise.all(
      [
        ...(driver.driverLicense.images ?? []),
        ...(driver.insurance.images ?? []),
        ...(driver.idLicense.images ?? []),
        ...(driver.vehicle.images ?? [])
      ].map(async (image) => {
        await onDownloadImage(image);
      })
    );
  };

  return (
    <div className={styles.driverInfo}>
      <PersonSummary
        personImage={driver.image}
        personFullName={`${driver.firstname} ${driver.lastname}`}
        personId={driver.id}
        statistic={[
          {
            label: t("Priority"),
            value: driver.isDeleted ? "-" : driver.priority + 1
          },
          { label: t("Order Capacity"), value: driver.capacity },
          { label: t("Weight"), value: driver.totalCargoWeight },
          { label: t("Volume"), value: driver.totalCargoVolume }
        ]}
      >
        <StatusControl
          isOpened={isStatusControlOpen}
          toggleOpen={toggleStatusControl}
          activeStatusIndex={driver.isActive ? 0 : 1}
          statusesList={statusesList}
          onClose={closeStatusControl}
          disabled={driver.isDeleted}
        />
      </PersonSummary>

      <DriverMainInfo driverDetails={driver} />
      <div className={styles.driverInfo_separatorLine} />
      <DriverCertificates
        driverDetails={driver}
        className={styles.driverInfo_mainInfoBlock}
      />
      <div className={styles.driverInfo_separatorLine} />

      <div className={styles.driverInfo_footer}>
        <Button
          type="button"
          title={t("Download Contacts List")}
          icon={<DocumentDownloadIcon width={24} height={24} color="#FFFFFF" />}
          data-testid="driverInfo--downloadBtn"
          disabled={!driver.hasProvidedContactsList}
          onClick={() => mutate(driver.id)}
        />

        <Button
          type="button"
          variant="default"
          title={t("Download all documents")}
          icon={<DownloadIcon width={24} height={24} />}
          disabled={!driver.hasProvidedContactsList}
          onClick={handleDocumentsDownload}
        />
      </div>
    </div>
  );
};

export default DriverInfoContent;
